// src/features/admin/hooks/useAdminProducts.js
import { useState, useEffect, useCallback } from 'react';
import { AdminProductService } from '../services/adminProductService';

export const useAdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  // Fetch all products
  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await AdminProductService.getAllProductsAdmin();
      setProducts(data);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch product statistics
  const fetchStats = async () => {
    try {
      const statsData = await AdminProductService.getProductStats();
      setStats(statsData);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  // Add new product
  const addProduct = async (productData, imageFile = null) => {
    try {
      setLoading(true);
      setError(null);
      const newProduct = await AdminProductService.addProduct(productData, imageFile);
      setProducts(prev => [newProduct, ...prev]);
      await fetchStats(); // Update stats
      return newProduct;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update product
  const updateProduct = async (productId, productData, imageFile = null) => {
    try {
      setLoading(true);
      setError(null);
      const updatedProduct = await AdminProductService.updateProduct(productId, productData, imageFile);
      setProducts(prev => 
        prev.map(product => 
          product.id === productId ? updatedProduct : product
        )
      );
      await fetchStats(); // Update stats
      return updatedProduct;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Delete product
  const deleteProduct = async (productId) => {
    try {
      setLoading(true);
      setError(null);
      await AdminProductService.deleteProduct(productId);
      setProducts(prev => prev.filter(product => product.id !== productId));
      await fetchStats(); // Update stats
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Toggle stock status
  const toggleStock = async (productId) => {
    try {
      const newStockStatus = await AdminProductService.toggleProductStock(productId);
      setProducts(prev =>
        prev.map(product =>
          product.id === productId
            ? { ...product, inStock: newStockStatus }
            : product
        )
      );
      await fetchStats(); // Update stats
      return newStockStatus;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Toggle featured status
  const toggleFeatured = async (productId) => {
    try {
      const newFeaturedStatus = await AdminProductService.toggleFeaturedStatus(productId);
      setProducts(prev =>
        prev.map(product =>
          product.id === productId
            ? { ...product, featured: newFeaturedStatus }
            : product
        )
      );
      await fetchStats(); // Update stats
      return newFeaturedStatus;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Search products
  const searchProducts = async (searchTerm) => {
    try {
      setLoading(true);
      setError(null);
      const results = await AdminProductService.searchProductsAdmin(searchTerm);
      setProducts(results);
    } catch (err) {
      setError(err.message);
      console.error('Error searching products:', err);
    } finally {
      setLoading(false);
    }
  };

  // Bulk update products
  const bulkUpdate = async (updates) => {
    try {
      setLoading(true);
      setError(null);
      await AdminProductService.bulkUpdateProducts(updates);
      await fetchProducts(); // Refetch all products
      await fetchStats(); // Update stats
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Clear error
  const clearError = () => setError(null);

  // Refresh data
  const refresh = async () => {
    await fetchProducts();
    await fetchStats();
  };

  // Initial load
  useEffect(() => {
    fetchProducts();
    fetchStats();
  }, []);

  return {
    products,
    loading,
    error,
    stats,
    addProduct,
    updateProduct,
    deleteProduct,
    toggleStock,
    toggleFeatured,
    searchProducts,
    bulkUpdate,
    clearError,
    refresh,
    fetchProducts
  };
};

// Hook for single product management
export const useAdminProduct = (productId) => {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchProduct = useCallback(async () => {
    if (!productId) return;

    try {
      setLoading(true);
      setError(null);
      const allProducts = await AdminProductService.getAllProductsAdmin();
      const foundProduct = allProducts.find(p => p.id === productId);
      setProduct(foundProduct || null);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching product:', err);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  return {
    product,
    loading,
    error,
    refresh: fetchProduct
  };
};